import type { documentTableOptions } from "./documentTableOptions";
import type { useTableSigning } from "./useTableSigning";
type Options = Pick<
  ReturnType<typeof documentTableOptions>,
  "store" | "onFolderSelect" | "folders" | "selectedFolderIds"
> &
  Pick<ReturnType<typeof useTableSigning>, "allDocuments">;
export function createTableSelection(options: Options) {
  const { store, allDocuments, onFolderSelect, folders, selectedFolderIds } = options;
  // Handle select all — select every document row shown AND every folder row.
  //
  // We select the displayed rows by id rather than calling store.selectAll().
  // store.selectAll() only iterates store.documents (the /documents MST map);
  // on /projects the documents live in the projectDocuments cache and
  // store.documents is empty, so store.selectAll() there selected nothing —
  // the reported "select all does nothing" bug. `allDocuments` is the rows
  // actually rendered (regular docs + container files) on every surface.
  //
  // onFolderSelect cascades into each folder's subtree (descendant folders +
  // their docs), so files inside subfolders the user never navigated into are
  // covered too.
  const handleSelectAll = (isSelected: boolean) => {
    if (store) {
      if (isSelected) {
        for (const doc of allDocuments) {
          store.selectDocument(doc.id, true);
        }
      } else {
        store.clearSelection();
      }
    }
    if (onFolderSelect) {
      for (const folder of folders) {
        onFolderSelect(folder.id, isSelected);
      }
    }
  };

  // Calculate selection state for the header checkbox. It covers BOTH the
  // document rows and the folder rows. We check the displayed docs by id
  // rather than reading store.allSelected: on /projects the docs live in the
  // projectDocuments cache, not the store.documents MST map, so a store-level
  // count always reads empty there. A folders-only view (no docs at the
  // current level) must still read "fully checked" once every folder is.
  const { hasDocRows, allDocRowsSelected, someDocRowsSelected } = documentSelection(options);
  const hasFolderRows = folders.length > 0;
  const allFolderRowsSelected =
    hasFolderRows && folders.every((folder) => selectedFolderIds.includes(folder.id));
  const allVisibleSelected =
    (hasDocRows || hasFolderRows) &&
    (!hasDocRows || allDocRowsSelected) &&
    (!hasFolderRows || allFolderRowsSelected);
  const someVisibleSelected = someDocRowsSelected || selectedFolderIds.length > 0;
  return { handleSelectAll, allVisibleSelected, someVisibleSelected };
}

function documentSelection({ allDocuments, store }: Options) {
  const hasDocRows = allDocuments.length > 0;
  const allDocRowsSelected =
    hasDocRows && !!store && allDocuments.every((doc) => store.isSelected(doc.id));
  const someDocRowsSelected =
    hasDocRows && !!store && allDocuments.some((doc) => store.isSelected(doc.id));
  return { hasDocRows, allDocRowsSelected, someDocRowsSelected };
}
